import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Switch,
  FormControlLabel,
  Collapse,
  Divider,
  Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { setAdminNonMemberView } from '../store/slices/uiSlice';
import { Member, MemberRole, MemberStatus } from '../types';
import { useSnackbar } from '../components/common/SnackbarProvider';
import HouseholdDialog from '../components/HouseholdDialog';
import AddFamilyMemberDialog from '../components/AddFamilyMemberDialog';
import NonMemberFamilyDialog from '../components/NonMemberFamilyDialog';

// Phone number formatting utility
const formatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');

  // Format as XXX-XXX-XXXX
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 6) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  } else {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  }
};

const AdminDashboardPage: React.FC = () => {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [managementView, setManagementView] = useState<string>('members'); // 'members' or 'staff'
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openHouseholdDialog, setOpenHouseholdDialog] = useState(false);
  const [familyDialog, setFamilyDialog] = useState<{ open: boolean; primaryId: string; primaryName: string }>({ open: false, primaryId: '', primaryName: '' });
  // Non-member equivalent of familyDialog: targets a NonMemberFamily add for the
  // given non-member (Member row whose membership_status starts with non_member_).
  const [nonMemberFamilyDialog, setNonMemberFamilyDialog] = useState<{ open: boolean; nonMemberId: string; nonMemberName: string }>({ open: false, nonMemberId: '', nonMemberName: '' });
  const [nonMemberFamily, setNonMemberFamily] = useState<Record<string, { loaded: boolean; rows: any[] }>>({});
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openNonMemberEditDialog, setOpenNonMemberEditDialog] = useState(false);
  const [editingNonMember, setEditingNonMember] = useState<any>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // Rows auto-expanded by the current search. Kept separate from expandedRows so
  // that clearing the search collapses these without touching manual opens.
  const [searchExpanded, setSearchExpanded] = useState<Set<string>>(new Set());
  // Members-view: which individual roster members have their inline details open.
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());

  // Password Reset Dialog State
  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);
  const [resetPasswordMember, setResetPasswordMember] = useState<{ id: string; name: string } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { token, user } = useAppSelector((state) => state.auth);
  // Non-admin staff get a READ-ONLY directory (look up contact info); only
  // admins/superusers can add/edit/manage.
  const isAdmin = user?.role === 'admin' || user?.role === 'superuser';
  const dispatch = useAppDispatch();

  // Add Member Form State
  const initialMemberState = {
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    mobile_phone: '',
    home_phone: '',
    work_phone: '',
    date_of_birth: '',
    joined_date: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    membership_tier_id: '', // no tier by default (flat-rate club; tiers optional)
    role: 'MEMBER' as string,
    departments: [] as string[],
    member_number: '',
    member_type: 'PRIMARY' as string,
    primary_member_id: '',
    relationship_to_primary: '',
  };
  const [newMember, setNewMember] = useState(initialMemberState);
  const [primaryMembers, setPrimaryMembers] = useState<Member[]>([]);
  const [tiers, setTiers] = useState<Array<{ id: number; tier_name: string }>>([]);

  // Edit Member Form State
  const [editMember, setEditMember] = useState<any>(null);

  // Compute relationship options based on member type for Add Member
  const relationshipOptions = useMemo(() => {
    console.log('Computing relationship options for member_type:', newMember.member_type);
    if (newMember.member_type === 'DEPENDENT') {
      return [
        { value: 'CHILD', label: 'Child' },
        { value: 'OTHER', label: 'Other' }
      ];
    } else if (newMember.member_type === 'SECONDARY') {
      return [
        { value: 'SPOUSE', label: 'Spouse' },
        { value: 'PARTNER', label: 'Partner' },
        { value: 'OTHER', label: 'Other' }
      ];
    }
    // Default fallback
    return [
      { value: 'SPOUSE', label: 'Spouse' },
      { value: 'PARTNER', label: 'Partner' },
      { value: 'CHILD', label: 'Child' },
      { value: 'OTHER', label: 'Other' }
    ];
  }, [newMember.member_type]);

  // Compute relationship options based on member type for Edit Member
  const editRelationshipOptions = useMemo(() => {
    if (!editMember) return [];
    console.log('Computing edit relationship options for member_type:', editMember.member_type);
    if (editMember.member_type === 'DEPENDENT') {
      return [
        { value: 'CHILD', label: 'Child' },
        { value: 'OTHER', label: 'Other' }
      ];
    } else if (editMember.member_type === 'SECONDARY') {
      return [
        { value: 'SPOUSE', label: 'Spouse' },
        { value: 'PARTNER', label: 'Partner' },
        { value: 'OTHER', label: 'Other' }
      ];
    }
    // Default fallback
    return [
      { value: 'SPOUSE', label: 'Spouse' },
      { value: 'PARTNER', label: 'Partner' },
      { value: 'CHILD', label: 'Child' },
      { value: 'OTHER', label: 'Other' }
    ];
  }, [editMember?.member_type]);

  // Fetch all members
  const fetchMembers = async () => {
    setLoading(true);
    try {
      // Call different endpoint based on management view
      let response;
      if (managementView === 'staff') {
        // Fetch staff from dedicated staff endpoint
        response = await axiosInstance.get('/staff/', { params: { include_inactive: 'true' } });
        console.log('Received staff:', response.data.data);
      } else if (managementView === 'non-members') {
        // Fetch non-members from non-member endpoint
        response = await axiosInstance.get('/non-members', { params: { include_inactive: 'true' } });
        console.log('Received non-members:', response.data.data);
      } else {
        // Fetch members (no longer includes staff)
        response = await axiosInstance.get('/members/', { params: { include_inactive: 'true' } });
        console.log('Received members:', response.data.data);
      }

      console.log(`Total ${managementView}:`, response.data.data.length);
      console.log(`Active ${managementView}:`, response.data.data.filter((m: any) => m.is_active !== false).length);
      console.log(`Inactive ${managementView}:`, response.data.data.filter((m: any) => m.is_active === false).length);

      // Log each entry's role individually
      response.data.data.forEach((m: any) => {
        console.log(`📧 ${m.email} → Role: "${m.role}" (type: ${typeof m.role})`);
      });

      // Store all members/staff
      setMembers(response.data.data);

      // Filter based on toggle
      const filtered = showDeactivated
        ? response.data.data.filter((m: any) => m.is_active === false)
        : response.data.data.filter((m: any) => m.is_active !== false);

      setFilteredMembers(filtered);

      // Extract primary members/non-members for the dropdown
      if (managementView === 'members') {
        const primaries = response.data.data.filter((m: any) =>
          m.is_active !== false &&
          (m.role === 'MEMBER' || m.role === 'member') &&
          (m.member_type === 'PRIMARY' || m.member_type === 'primary' || !m.member_type)
        );
        setPrimaryMembers(primaries);
      } else if (managementView === 'non-members') {
        // For non-members, get primary non-members for household dropdown
        const primaries = response.data.data.filter((m: any) =>
          m.is_active !== false &&
          (m.member_type === 'PRIMARY' || m.member_type === 'primary' || !m.member_type)
        );
        setPrimaryMembers(primaries);
      }
    } catch (err: any) {
      console.error(`Fetch ${managementView} error:`, err.response?.data);
      snackbar.error(err.response?.data?.message || `Failed to fetch ${managementView}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('Current user:', user);
    console.log('User role:', user?.role);
    fetchMembers();
  }, []);

  // Load membership tiers for the create/edit dialogs. This club is flat-rate, so
  // there may be none configured — then the tier field is simply hidden and members
  // are created without a tier (the API only validates a tier when one is supplied).
  useEffect(() => {
    axiosInstance
      .get('/membership-tiers/')
      .then((resp) =>
        setTiers((resp.data?.data || []).map((t: any) => ({ id: t.id, tier_name: t.tier_name })))
      )
      .catch(() => setTiers([]));
  }, []);

  // Refetch members when showDeactivated toggle changes
  useEffect(() => {
    fetchMembers();
  }, [showDeactivated]);

  // Refetch members when managementView changes to ensure fresh data
  useEffect(() => {
    fetchMembers();
  }, [managementView]);

  // Tint the global header bar orange while viewing Non-Member Management
  // (green = members, orange = non-members). Reset on leave.
  useEffect(() => {
    dispatch(setAdminNonMemberView(managementView === 'non-members'));
    return () => {
      dispatch(setAdminNonMemberView(false));
    };
  }, [managementView, dispatch]);

  // Search filter (frontend filtering)
  useEffect(() => {
    let filtered = members;

    // Filter by active/inactive status based on toggle
    filtered = showDeactivated
      ? filtered.filter((m: any) => m.is_active === false)
      : filtered.filter((m: any) => m.is_active !== false);

    // Filter by management view (members vs staff vs non-members)
    if (managementView === 'members') {
      // Show only MEMBER role
      filtered = filtered.filter((m: any) =>
        m.role && m.role.toLowerCase() === 'member'
      );
    } else if (managementView === 'staff') {
      // Show only STAFF, ADMIN, and SUPERUSER roles
      filtered = filtered.filter((m: any) =>
        m.role && ['staff', 'admin', 'superuser'].includes(m.role.toLowerCase())
      );
    }
    // For non-members, no additional filtering needed - API returns only non-members

    // Filter by search query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      // First, find all members that match the search query (by number, name, email,
      // or — for non-members — by any of their NonMemberFamily dependent's names,
      // which the API now serializes onto each non-member as `family_names`).
      const matchedMembers = members.filter((m: any) =>
        (m.member_number && m.member_number.toLowerCase().includes(q)) ||
        (m.first_name || m.firstName || '').toLowerCase().includes(q) ||
        (m.last_name || m.lastName || '').toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        (Array.isArray(m.family_names) && m.family_names.some((n: string) => (n || '').toLowerCase().includes(q)))
      );

      if (matchedMembers.length > 0) {
        // For each matched member, include their entire household
        const householdMemberIds = new Set<string>();

        matchedMembers.forEach((matchedMember: any) => {
          // If the matched member is PRIMARY, find all their household members
          if (matchedMember.member_type === 'primary' || matchedMember.member_type === 'PRIMARY') {
            householdMemberIds.add(matchedMember.id);
            // Find all members who have this member as their primary
            members.forEach((m: any) => {
              if (m.primary_member_id === matchedMember.id) {
                householdMemberIds.add(m.id);
              }
            });
          } else if (matchedMember.primary_member_id) {
            // If the matched member is SECONDARY/DEPENDENT, find their primary and all siblings
            householdMemberIds.add(matchedMember.id);
            householdMemberIds.add(matchedMember.primary_member_id);
            // Find all other members with the same primary
            members.forEach((m: any) => {
              if (m.primary_member_id === matchedMember.primary_member_id) {
                householdMemberIds.add(m.id);
              }
            });
          } else {
            // Standalone member
            householdMemberIds.add(matchedMember.id);
          }
        });

        // Filter to show only household members, respecting active/inactive toggle
        filtered = filtered.filter((m: any) => householdMemberIds.has(m.id));

        // Auto-expand the primaries of the matched households so the matched person
        // is visible (the list shows primaries only). Tracked separately from manual
        // opens so clearing the search collapses exactly these rows. The primary id
        // is the match's own id when it's a primary, otherwise its primary_member_id.
        if (managementView === 'members' || managementView === 'non-members') {
          const expandIds = new Set<string>();
          matchedMembers.forEach((matchedMember: any) => {
            const primaryId = matchedMember.primary_member_id || matchedMember.id;
            expandIds.add(primaryId);
            // For non-members, expansion is where the NonMemberFamily roster is
            // rendered — lazy-load it so the matched dependent name actually shows
            // up under the auto-expanded primary instead of "expand to load…".
            if (managementView === 'non-members' && !nonMemberFamily[primaryId]) {
              void loadNonMemberFamily(primaryId);
            }
          });
          setSearchExpanded(expandIds);
        } else {
          setSearchExpanded(new Set());
        }
      } else {
        // No matches found - show empty list and clear any search auto-expand.
        filtered = [];
        setSearchExpanded(new Set());
      }
    } else {
      // Empty query: nothing is auto-expanded (manual opens in expandedRows persist).
      setSearchExpanded(new Set());
    }

    setFilteredMembers(filtered);
  }, [searchQuery, managementView, members, showDeactivated]);

  // Handle closing add member dialog and reset form
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewMember(initialMemberState);
  };

  // Add new member
  const handleAddMember = async () => {

    // Client-side validation
    // For dependents, only first_name and last_name are required
    if (!newMember.first_name || !newMember.last_name) {
      snackbar.error('Please fill in all required fields');
      return;
    }

    // For non-dependents, email and password are also required
    if (newMember.member_type !== 'DEPENDENT') {
      if (!newMember.email || !newMember.password) {
        snackbar.error('Please fill in all required fields');
        return;
      }
    }

    try {
      // Prepare registration data
      const registrationData: any = {
        email: newMember.email.trim(),
        password: newMember.password,
        first_name: newMember.first_name.trim(),
        last_name: newMember.last_name.trim(),
        mobile_phone: newMember.mobile_phone?.trim() || '',
        home_phone: newMember.home_phone?.trim() || '',
        work_phone: newMember.work_phone?.trim() || '',
        date_of_birth: newMember.date_of_birth || null,
        joined_date: newMember.joined_date || null,
        address_line1: newMember.address_line1?.trim() || '',
        address_line2: newMember.address_line2?.trim() || '',
        city: newMember.city?.trim() || '',
        state: newMember.state?.trim() || '',
        postal_code: newMember.postal_code?.trim() || '',
        membership_tier_id: newMember.role === 'MEMBER' && newMember.membership_tier_id ? parseInt(newMember.membership_tier_id) : null,
        role: newMember.role, // Include role in registration
        departments: managementView === 'staff' ? newMember.departments : undefined,
        member_number: newMember.member_number?.trim() || undefined, // Include custom member number if provided
      };

      // Add hierarchy fields for MEMBER role
      if (newMember.role === 'MEMBER') {
        registrationData.member_type = newMember.member_type;
        if (newMember.member_type !== 'PRIMARY') {
          registrationData.primary_member_id = newMember.primary_member_id || undefined;
          registrationData.relationship_to_primary = newMember.relationship_to_primary || undefined;
        }
      }

      console.log('Sending registration data:', registrationData);

      // Call different endpoint based on management view
      let endpoint: string;
      if (managementView === 'staff') {
        endpoint = '/staff/';
      } else if (managementView === 'non-members') {
        endpoint = '/non-members/admin-create';
        // For non-members, include hierarchy fields for household structure
        registrationData.acquisition_source = 'admin_dashboard';
        delete registrationData.membership_tier_id;
        delete registrationData.role;
        // Keep member_type for non-member hierarchy (PRIMARY/SECONDARY/DEPENDENT)
        registrationData.member_type = newMember.member_type;
        if (newMember.member_type !== 'PRIMARY') {
          registrationData.primary_member_id = newMember.primary_member_id || undefined;
          registrationData.relationship_to_primary = newMember.relationship_to_primary || undefined;
        }
      } else {
        endpoint = '/members/admin-create';
      }
      const response = await axiosInstance.post(endpoint, registrationData);

      snackbar.success(`${managementView === 'staff' ? 'Staff' : managementView === 'non-members' ? 'Non-member' : 'Member'} added successfully!`);
      setOpenAddDialog(false);
      setNewMember(initialMemberState);
      fetchMembers();
    } catch (err: any) {
      console.error('Add member error:', err.response?.data);
      console.error('Full error:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to add member';
      snackbar.error(errorMessage);
    }
  };

  // Open edit dialog
  const handleOpenEditDialog = (member: any) => {
    // Convert enum values to uppercase names for consistency
    const memberType = member.member_type
      ? (typeof member.member_type === 'string' ? member.member_type.toUpperCase() : 'PRIMARY')
      : 'PRIMARY';

    const relationshipType = member.relationship_to_primary
      ? (typeof member.relationship_to_primary === 'string' ? member.relationship_to_primary.toUpperCase() : '')
      : '';

    setEditMember({
      id: member.id,
      email: member.email,
      first_name: member.first_name || member.firstName,
      last_name: member.last_name || member.lastName,
      mobile_phone: member.mobile_phone || '',
      home_phone: member.home_phone || '',
      work_phone: member.work_phone || '',
      date_of_birth: member.date_of_birth || '',
      // Staff have 'hire_date', members have 'joined_date'
      joined_date: member.hire_date || member.joined_date || '',
      address_line1: member.address_line1 || '',
      address_line2: member.address_line2 || '',
      city: member.city || '',
      state: member.state || '',
      postal_code: member.postal_code || '',
      membership_tier_id: member.membership_tier_id?.toString() || member.membershipTierId?.toString() || '',
      role: member.role.toUpperCase(),
      // Convert departments to lowercase to match MenuItem values
      departments: member.departments ? member.departments.map((d: string) => d.toLowerCase()) : [],
      member_number: managementView === 'staff' ? (member.employee_number || '') : (member.member_number || ''),
      member_type: memberType,
      primary_member_id: member.primary_member_id || '',
      relationship_to_primary: relationshipType,
    });
    setOpenEditDialog(true);
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditMember(null);
  };

  // Update member
  const handleUpdateMember = async () => {

    // Client-side validation
    if (!editMember.email || !editMember.first_name || !editMember.last_name) {
      snackbar.error('Please fill in all required fields');
      return;
    }

    try {
      // Prepare update data
      const updateData: any = {
        email: editMember.email.trim(),
        first_name: editMember.first_name.trim(),
        last_name: editMember.last_name.trim(),
        mobile_phone: editMember.mobile_phone?.trim() || '',
        home_phone: editMember.home_phone?.trim() || '',
        work_phone: editMember.work_phone?.trim() || '',
        date_of_birth: editMember.date_of_birth || null,
        joined_date: editMember.joined_date || null,
        address_line1: editMember.address_line1?.trim() || '',
        address_line2: editMember.address_line2?.trim() || '',
        city: editMember.city?.trim() || '',
        state: editMember.state?.trim() || '',
        postal_code: editMember.postal_code?.trim() || '',
        membership_tier_id: editMember.role === 'MEMBER' && editMember.membership_tier_id ? parseInt(editMember.membership_tier_id) : null,
        role: editMember.role,
        ...(managementView === 'staff'
          ? {
              employee_number: editMember.member_number?.trim() || undefined,
              departments: editMember.departments || []
            }
          : { member_number: editMember.member_number?.trim() || undefined }
        ),
      };

      // Add hierarchy fields for MEMBER role
      if (editMember.role === 'MEMBER') {
        updateData.member_type = editMember.member_type;
        if (editMember.member_type !== 'PRIMARY') {
          updateData.primary_member_id = editMember.primary_member_id || undefined;
          updateData.relationship_to_primary = editMember.relationship_to_primary || undefined;
        } else {
          // Explicitly set to null for PRIMARY members
          updateData.primary_member_id = null;
          updateData.relationship_to_primary = null;
        }
      }

      console.log('Sending update data:', updateData);

      // Call different endpoint based on management view
      const endpoint = managementView === 'staff' ? `/staff/${editMember.id}` : `/members/${editMember.id}`;
      await axiosInstance.put(endpoint, updateData);

      snackbar.success(`${managementView === 'staff' ? 'Staff' : 'Member'} updated successfully!`);
      setOpenEditDialog(false);
      setEditMember(null);
      fetchMembers();
    } catch (err: any) {
      console.error('Update member error:', err.response?.data);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to update member';
      snackbar.error(errorMessage);
    }
  };

  // Lock account
  const handleLockAccount = async (memberId: string, memberName: string) => {
    const reason = window.prompt(`Why are you locking ${memberName}'s account?`, 'Security concern');
    if (!reason) return;

    try {
      const endpoint = managementView === 'staff' ? `/staff/${memberId}/lock` : `/members/${memberId}/lock`;
      await axiosInstance.post(endpoint, { reason });
      snackbar.success('Account locked successfully!');
      fetchMembers();
    } catch (err: any) {
      console.error('Lock account error:', err.response?.data);
      snackbar.error(err.response?.data?.message || err.response?.data?.error || 'Failed to lock account');
    }
  };

  // Unlock account
  const handleUnlockAccount = async (memberId: string, memberName: string) => {
    if (!window.confirm(`Are you sure you want to unlock ${memberName}'s account?`)) return;

    try {
      const endpoint = managementView === 'staff' ? `/staff/${memberId}/unlock` : `/members/${memberId}/unlock`;
      await axiosInstance.post(endpoint);
      snackbar.success('Account unlocked successfully!');
      fetchMembers();
    } catch (err: any) {
      console.error('Unlock account error:', err.response?.data);
      snackbar.error(err.response?.data?.message || err.response?.data?.error || 'Failed to unlock account');
    }
  };

  // Delete member (soft delete - deactivate)
  const handleDeleteMember = async (memberId: string, memberName: string) => {
    const entityType = managementView === 'staff' ? 'staff member' : 'member';
    if (!window.confirm(`Are you sure you want to deactivate ${memberName}?`)) return;

    try {
      const endpoint = managementView === 'staff' ? `/staff/${memberId}` : `/members/${memberId}`;
      await axiosInstance.delete(endpoint);
      snackbar.success(`${managementView === 'staff' ? 'Staff' : 'Member'} deactivated successfully!`);
      fetchMembers();
    } catch (err: any) {
      console.error(`Delete ${entityType} error:`, err.response?.data);
      console.error('Full error:', err);
      snackbar.error(err.response?.data?.message || err.response?.data?.error || `Failed to deactivate ${entityType}`);
    }
  };

  // Reactivate member (superuser only)
  const handleReactivateMember = async (memberId: string, memberName: string) => {
    const entityType = managementView === 'staff' ? 'staff member' : 'member';
    if (!window.confirm(`Are you sure you want to reactivate ${memberName}?`)) return;

    try {
      const endpoint = managementView === 'staff' ? `/staff/${memberId}/status` : `/members/${memberId}/status`;
      await axiosInstance.put(endpoint, {
        status: managementView === 'non-members' ? 'NON_MEMBER_ACTIVE' : 'ACTIVE',
        reason: `Reactivated by superuser: ${user?.email}`
      });
      snackbar.success(`${managementView === 'staff' ? 'Staff' : 'Member'} reactivated successfully!`);
      fetchMembers();
    } catch (err: any) {
      console.error(`Reactivate ${entityType} error:`, err.response?.data);
      snackbar.error(err.response?.data?.message || err.response?.data?.error || `Failed to reactivate ${entityType}`);
    }
  };

  // Permanent delete member (superuser only)
  const handlePermanentDelete = async (memberId: string, memberName: string) => {
    const entityType = managementView === 'staff' ? 'staff member' : 'member';
    if (!window.confirm(`⚠️ PERMANENT DELETE ⚠️\n\nAre you ABSOLUTELY SURE you want to PERMANENTLY delete ${memberName}?\n\nThis action CANNOT be undone and will remove all ${entityType} data from the database.`)) return;

    try {
      const endpoint = managementView === 'staff' ? `/staff/${memberId}/permanent` : `/members/${memberId}/permanent`;
      await axiosInstance.delete(endpoint);
      snackbar.success(`${managementView === 'staff' ? 'Staff' : 'Member'} permanently deleted!`);
      fetchMembers();
    } catch (err: any) {
      console.error('Permanent delete error:', err.response?.data);
      snackbar.error(err.response?.data?.message || err.response?.data?.error || `Failed to permanently delete ${entityType}`);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'superuser':
        return 'error';
      case 'admin':
        return 'warning';
      case 'staff':
        return 'info';
      default:
        return 'default';
    }
  };

  // Adjust guest visits for a non-member
  const handleAdjustGuestVisits = async (member: any, adjustment: number) => {
    const action = adjustment > 0 ? 'add' : 'remove';
    const confirmMsg = adjustment > 0
      ? `Add 1 guest visit for ${member.first_name} ${member.last_name}?`
      : `Remove 1 guest visit from ${member.first_name} ${member.last_name}?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      await axiosInstance.post(`/non-members/${member.id}/guest-visits/adjust`, {
        adjustment: adjustment,
        reason: `Manual adjustment from Admin Dashboard`
      });
      snackbar.success(`Guest visit ${action === 'add' ? 'added' : 'removed'} successfully`);
      fetchMembers(); // Refresh to show updated count
    } catch (err: any) {
      snackbar.error(err.response?.data?.error || `Failed to ${action} guest visit`);
    }
  };

  // Open non-member edit dialog
  const handleEditNonMember = (member: any) => {
    setEditingNonMember({
      ...member,
      guest_annual_limit: member.guest_annual_limit || 6
    });
    setOpenNonMemberEditDialog(true);
  };

  // Save non-member settings
  const handleSaveNonMember = async () => {
    if (!editingNonMember) return;

    try {
      await axiosInstance.put(`/non-members/${editingNonMember.id}`, {
        guest_annual_limit: editingNonMember.guest_annual_limit,
        first_name: editingNonMember.first_name,
        last_name: editingNonMember.last_name,
        email: editingNonMember.email,
        phone: editingNonMember.phone
      });
      snackbar.success('Non-member settings updated successfully');
      setOpenNonMemberEditDialog(false);
      setEditingNonMember(null);
      fetchMembers();
    } catch (err: any) {
      snackbar.error(err.response?.data?.error || 'Failed to update non-member');
    }
  };

  const loadNonMemberFamily = async (nonMemberId: string) => {
    try {
      const resp = await axiosInstance.get(`/non-members/${nonMemberId}/family`);
      setNonMemberFamily((prev) => ({ ...prev, [nonMemberId]: { loaded: true, rows: resp.data?.data || [] } }));
    } catch {
      setNonMemberFamily((prev) => ({ ...prev, [nonMemberId]: { loaded: true, rows: [] } }));
    }
  };

  const toggleRowExpansion = (memberId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
        // Lazy-load a non-member's family (NonMemberFamily rows) on first expand.
        if (managementView === 'non-members' && !nonMemberFamily[memberId]) {
          loadNonMemberFamily(memberId);
        }
      }
      return newSet;
    });
  };

  // A row is open if it was opened manually OR auto-expanded by the current search.
  const isRowExpanded = (id: string) => expandedRows.has(id) || searchExpanded.has(id);

  // Members-view: toggle a single roster member's inline details panel.
  const toggleMemberDetails = (id: string) => {
    setExpandedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // ---- Household grouping helpers (Member Management) -----------------------
  // Compute age from an ISO date string (YYYY-MM-DD). Returns null if unparseable.
  const ageFromDob = (dob?: string): number | null => {
    if (!dob) return null;
    const parts = String(dob).split('-');
    if (parts.length < 3) return null;
    const [year, month, day] = parts.map((p) => parseInt(p, 10));
    if (!year || !month || !day) return null;
    const today = new Date();
    let age = today.getFullYear() - year;
    const hadBirthday =
      today.getMonth() + 1 > month ||
      (today.getMonth() + 1 === month && today.getDate() >= day);
    if (!hadBirthday) age -= 1;
    return age >= 0 && age < 150 ? age : null;
  };

  const memberFullName = (m: any) =>
    `${m.first_name || m.firstName || ''} ${m.last_name || m.lastName || ''}`.trim();

  // Household members (SECONDARY spouses + DEPENDENT children) for a given primary,
  // respecting the active/inactive toggle. SECONDARY are sorted before DEPENDENT.
  const householdMembersOf = (primaryId: string): any[] => {
    const rank = (m: any) => {
      const t = (m.member_type || '').toString().toUpperCase();
      if (t === 'SECONDARY') return 0;
      if (t === 'DEPENDENT') return 1;
      return 2;
    };
    return members
      .filter((m: any) => m.primary_member_id === primaryId)
      .filter((m: any) => (showDeactivated ? m.is_active === false : m.is_active !== false))
      .sort((a: any, b: any) => {
        const r = rank(a) - rank(b);
        return r !== 0 ? r : memberFullName(a).localeCompare(memberFullName(b));
      });
  };

  // The members view groups by household: the top-level list shows only PRIMARIES
  // (rows with a falsy primary_member_id). Staff/non-members keep the flat list.
  const displayList =
    managementView === 'members'
      ? filteredMembers.filter((m: any) => !m.primary_member_id)
      : filteredMembers;

  // Members-view summary: households = primaries shown, people = primaries + their
  // household members (active/inactive per the toggle).
  const householdCount = displayList.length;
  const peopleCount = displayList.reduce(
    (sum: number, p: any) => sum + 1 + householdMembersOf(p.id).length,
    0
  );

  // Behavior-preserving extraction of the per-row action cluster. Used for both the
  // top-level primary rows and the household sub-rows, in both mobile and desktop
  // renderings. Role gating and handlers are identical to the previous inline code.
  const renderMemberActions = (member: any, size: 'small' | 'medium' = 'medium') => {
    const fs = size === 'small' ? 'small' : undefined;
    const name = `${member.first_name || member.firstName} ${member.last_name || member.lastName}`;
    const canManage =
      (user?.role === 'admin' || user?.role === 'superuser') &&
      !(user?.role === 'admin' && member.role === 'superuser');

    if (member.is_active === false || member.membership_status === 'CANCELLED') {
      // Deactivated member - show reactivate and permanent delete (superuser only)
      return user?.role === 'superuser' ? (
        <>
          <IconButton
            size="small"
            color="success"
            onClick={() => handleReactivateMember(member.id, name)}
            title="Reactivate Member"
          >
            <RestoreIcon fontSize={fs} />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handlePermanentDelete(member.id, name)}
            title="Permanently Delete Member"
          >
            <DeleteForeverIcon fontSize={fs} />
          </IconButton>
        </>
      ) : null;
    }

    // Active member - show edit, (add family), reset password, lock/unlock, deactivate
    return (
      <>
        {/* View detail — opens the dedicated household/detail page for this person
            (members: /members/:id; non-members: /non-members/:id). Staff view has no
            detail page yet, so the icon is omitted there to avoid a dead link. */}
        {managementView !== 'staff' && (
          <IconButton
            size="small"
            color="info"
            onClick={() =>
              navigate(managementView === 'non-members' ? `/non-members/${member.id}` : `/members/${member.id}`)
            }
            title="View detail"
          >
            <OpenInNewIcon fontSize={fs} />
          </IconButton>
        )}

        {/* Edit button - available for admin and superuser, but admins can't edit superusers */}
        {canManage && (
          <IconButton
            size="small"
            color="primary"
            onClick={() => (managementView === 'non-members' ? handleEditNonMember(member) : handleOpenEditDialog(member))}
            title={managementView === 'non-members' ? 'Edit Non-Member Settings' : 'Edit Member'}
          >
            <EditIcon fontSize={fs} />
          </IconButton>
        )}

        {/* Add family member — link a dependent/partner to an existing PRIMARY */}
        {managementView === 'members' && !member.primary_member_id && canManage && (
          <IconButton
            size="small"
            color="primary"
            onClick={() =>
              setFamilyDialog({
                open: true,
                primaryId: member.id,
                primaryName: name,
              })
            }
            title="Add family member"
          >
            <PersonAddIcon fontSize={fs} />
          </IconButton>
        )}

        {/* Add family member (non-members) — adds a NonMemberFamily row to this
            non-member. Posts to /non-members/:id/family, not /members/admin-create. */}
        {isAdmin && managementView === 'non-members' && (
          <IconButton
            size="small"
            color="primary"
            onClick={() =>
              setNonMemberFamilyDialog({
                open: true,
                nonMemberId: member.id,
                nonMemberName: name,
              })
            }
            title="Add family member"
          >
            <PersonAddIcon fontSize={fs} />
          </IconButton>
        )}

        {/* Reset Password — works on any row in the members table,
            including non-members (their password_hash is in the same column). */}
        {canManage && (
          <IconButton
            size="small"
            color="secondary"
            onClick={() => handleOpenResetPasswordDialog(member.id, name)}
            title="Reset Password"
          >
            <VpnKeyIcon fontSize={fs} />
          </IconButton>
        )}

        {(member.is_locked || member.isLocked)
          ? // Locked account - show red locked icon (clickable to unlock). Admins can't unlock superusers.
            canManage && (
              <IconButton
                size="small"
                color="error"
                onClick={() => handleUnlockAccount(member.id, name)}
                title="Unlock Account"
              >
                <LockIcon fontSize={fs} />
              </IconButton>
            )
          : // Unlocked account - show green unlocked icon (clickable to lock). Admins can't lock superusers.
            canManage && (
              <IconButton
                size="small"
                color="success"
                onClick={() => handleLockAccount(member.id, name)}
                title="Lock Account"
              >
                <LockOpenIcon fontSize={fs} />
              </IconButton>
            )}

        {/* Deactivate button - admins can't deactivate superusers */}
        {canManage && (
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteMember(member.id, name)}
            title="Deactivate Member"
          >
            <DeleteIcon fontSize={fs} />
          </IconButton>
        )}
      </>
    );
  };

  // Format an ISO date (YYYY-MM-DD) as MM/DD/YYYY. Returns '' if unparseable.
  const formatDate = (value?: string): string => {
    if (!value) return '';
    const [year, month, day] = String(value).split('-');
    if (!year || !month || !day) return '';
    return `${month}/${day}/${year}`;
  };

  // A members-view roster row: a clickable name that reveals that member's full
  // details inline. Used for the primary AND each household member. Actions are
  // shown only for spouse/dependents (the primary keeps its header-row actions).
  const renderRosterRow = (
    member: any,
    opts: { showActions: boolean; size: 'small' | 'medium' }
  ) => {
    const { showActions, size } = opts;
    const age = ageFromDob(member.date_of_birth);
    const isPrimary = !member.primary_member_id;
    const rel = isPrimary
      ? 'primary'
      : member.relationship_to_primary
      ? String(member.relationship_to_primary).toLowerCase()
      : '';
    const hasEmail = member.email && !String(member.email).endsWith('@placeholder.local');
    const open = expandedMembers.has(member.id);
    const birthday = formatDate(member.date_of_birth);
    const memberSince = formatDate(member.joined_date);
    const address =
      member.address_line1 || member.address_line2 || member.city || member.state || member.postal_code
        ? `${member.address_line1 || ''}${member.address_line2 ? ', ' + member.address_line2 : ''}${member.city ? ', ' + member.city : ''}${member.state ? ', ' + member.state : ''} ${member.postal_code || ''}`.trim()
        : '';

    return (
      <Box key={member.id} sx={{ py: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Link
              component="button"
              type="button"
              underline="hover"
              color="inherit"
              onClick={() => toggleMemberDetails(member.id)}
              sx={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                fontWeight: 500,
                fontSize: size === 'small' ? '0.875rem' : '1rem',
                textAlign: 'left',
                wordBreak: 'break-word',
              }}
            >
              {memberFullName(member)}
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </Link>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
              {rel}
              {age != null ? ` · age ${age}` : ''}
            </Typography>
            {hasEmail && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                {member.email}
              </Typography>
            )}
            {member.mobile_phone && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                {member.mobile_phone}
              </Typography>
            )}
          </Box>
          {showActions && (
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>{renderMemberActions(member, size)}</Box>
          )}
        </Box>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack spacing={1} sx={{ mt: 1, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
            {member.member_number && (
              <Box>
                <Typography variant="caption" color="text.secondary">Member Number</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{member.member_number}</Typography>
              </Box>
            )}
            {birthday && (
              <Box>
                <Typography variant="caption" color="text.secondary">Birthday</Typography>
                <Typography variant="body2">
                  {birthday}{age != null ? ` (age ${age})` : ''}
                </Typography>
              </Box>
            )}
            {member.mobile_phone && (
              <Box>
                <Typography variant="caption" color="text.secondary">Mobile Phone</Typography>
                <Typography variant="body2">{member.mobile_phone}</Typography>
              </Box>
            )}
            {member.home_phone && (
              <Box>
                <Typography variant="caption" color="text.secondary">Home Phone</Typography>
                <Typography variant="body2">{member.home_phone}</Typography>
              </Box>
            )}
            {member.work_phone && (
              <Box>
                <Typography variant="caption" color="text.secondary">Work Phone</Typography>
                <Typography variant="body2">{member.work_phone}</Typography>
              </Box>
            )}
            {memberSince && (
              <Box>
                <Typography variant="caption" color="text.secondary">Member Since</Typography>
                <Typography variant="body2">{memberSince}</Typography>
              </Box>
            )}
            {address && (
              <Box>
                <Typography variant="caption" color="text.secondary">Address</Typography>
                <Typography variant="body2">{address}</Typography>
              </Box>
            )}
          </Stack>
        </Collapse>
      </Box>
    );
  };

  // A non-members-view roster row: a clickable name that reveals full details
  // inline. Used for the primary non-member (the Member row) AND each
  // NonMemberFamily member. No action buttons — the primary's actions stay on the
  // header row and family CRUD lives on the detail page. The expand key must be a
  // string: for the primary it's `member.id` (a UUID); for a family member it's
  // `nmf-${fm.id}` since those ids are numbers and must not collide with UUIDs.
  const renderNonMemberRosterRow = (
    person: any,
    opts: { isPrimary: boolean; size: 'small' | 'medium' }
  ) => {
    const { isPrimary, size } = opts;
    const key = isPrimary ? String(person.id) : `nmf-${person.id}`;
    const open = expandedMembers.has(key);
    const age = ageFromDob(person.date_of_birth);
    const rel = isPrimary
      ? 'primary'
      : person.relationship_type
      ? String(person.relationship_type).toLowerCase()
      : '';
    const birthday = formatDate(person.date_of_birth);
    const phone = isPrimary ? person.mobile_phone : person.phone;
    const hasEmail = person.email && !String(person.email).endsWith('@placeholder.local');
    const address = isPrimary
      ? (person.address_line1 || person.address_line2 || person.city || person.state || person.postal_code
          ? `${person.address_line1 || ''}${person.address_line2 ? ', ' + person.address_line2 : ''}${person.city ? ', ' + person.city : ''}${person.state ? ', ' + person.state : ''} ${person.postal_code || ''}`.trim()
          : '')
      : '';

    return (
      <Box key={key} sx={{ py: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Link
              component="button"
              type="button"
              underline="hover"
              color="inherit"
              onClick={() => toggleMemberDetails(key)}
              sx={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                fontWeight: 500,
                fontSize: size === 'small' ? '0.875rem' : '1rem',
                textAlign: 'left',
                wordBreak: 'break-word',
              }}
            >
              {memberFullName(person)}
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </Link>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
              {rel}
              {age != null ? ` · age ${age}` : ''}
            </Typography>
            {hasEmail && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                {person.email}
              </Typography>
            )}
            {phone && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                {phone}
              </Typography>
            )}
          </Box>
        </Box>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack spacing={1} sx={{ mt: 1, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
            {isPrimary && person.member_number && (
              <Box>
                <Typography variant="caption" color="text.secondary">Member Number</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{person.member_number}</Typography>
              </Box>
            )}
            {birthday && (
              <Box>
                <Typography variant="caption" color="text.secondary">Birthday</Typography>
                <Typography variant="body2">
                  {birthday}{age != null ? ` (age ${age})` : ''}
                </Typography>
              </Box>
            )}
            {hasEmail && (
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{person.email}</Typography>
              </Box>
            )}
            {isPrimary ? (
              <>
                {person.mobile_phone && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Mobile Phone</Typography>
                    <Typography variant="body2">{person.mobile_phone}</Typography>
                  </Box>
                )}
                {person.home_phone && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Home Phone</Typography>
                    <Typography variant="body2">{person.home_phone}</Typography>
                  </Box>
                )}
                {person.work_phone && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Work Phone</Typography>
                    <Typography variant="body2">{person.work_phone}</Typography>
                  </Box>
                )}
                {address && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Address</Typography>
                    <Typography variant="body2">{address}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Guest Visits</Typography>
                  <Typography variant="body2">
                    {person.guest_visits_this_year || 0} / {person.guest_annual_limit || 6}
                  </Typography>
                </Box>
                {person.membership_status && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Typography variant="body2">{person.membership_status}</Typography>
                  </Box>
                )}
              </>
            ) : (
              <>
                {person.phone && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                    <Typography variant="body2">{person.phone}</Typography>
                  </Box>
                )}
                {person.allergies && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Allergies</Typography>
                    <Typography variant="body2">{person.allergies}</Typography>
                  </Box>
                )}
                {person.medical_conditions && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Medical Conditions</Typography>
                    <Typography variant="body2">{person.medical_conditions}</Typography>
                  </Box>
                )}
                {(person.emergency_contact_name || person.emergency_contact_phone) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Emergency Contact</Typography>
                    <Typography variant="body2">
                      {[person.emergency_contact_name, person.emergency_contact_phone].filter(Boolean).join(' — ')}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Kids Club eligible</Typography>
                  <Typography variant="body2">{person.kids_club_eligible ? 'yes' : 'no'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Lessons eligible</Typography>
                  <Typography variant="body2">{person.lessons_eligible ? 'yes' : 'no'}</Typography>
                </Box>
              </>
            )}
          </Stack>
        </Collapse>
      </Box>
    );
  };

  // Non-members household roster: the primary non-member followed by their lazy-loaded
  // NonMemberFamily rows. Shared by the mobile-card and desktop-table renderings.
  const renderNonMemberRoster = (member: any, size: 'small' | 'medium') => {
    const entry = nonMemberFamily[member.id];
    return (
      <Stack divider={<Divider flexItem />}>
        {renderNonMemberRosterRow(member, { isPrimary: true, size })}
        {!entry?.loaded ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>Loading…</Typography>
        ) : entry.rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>No family members added.</Typography>
        ) : (
          entry.rows.map((fm: any) => renderNonMemberRosterRow(fm, { isPrimary: false, size }))
        )}
      </Stack>
    );
  };

  // Open password reset dialog
  const handleOpenResetPasswordDialog = (memberId: string, memberName: string) => {
    setResetPasswordMember({ id: memberId, name: memberName });
    setNewPassword('');
    setConfirmPassword('');
    setOpenResetPasswordDialog(true);
  };

  // Close password reset dialog
  const handleCloseResetPasswordDialog = () => {
    setOpenResetPasswordDialog(false);
    setResetPasswordMember(null);
    setNewPassword('');
    setConfirmPassword('');
  };

  // Reset password
  const handleResetPassword = async () => {

    // Validate password
    if (!newPassword) {
      snackbar.error('Please enter a new password');
      return;
    }

    if (newPassword.length < 8) {
      snackbar.error('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      snackbar.error('Passwords do not match');
      return;
    }

    if (!resetPasswordMember) return;

    try {
      const endpoint = managementView === 'staff'
        ? `/staff/${resetPasswordMember.id}/reset-password`
        : `/members/${resetPasswordMember.id}/reset-password`;

      await axiosInstance.post(endpoint, { new_password: newPassword });

      snackbar.success(`Password reset successfully for ${resetPasswordMember.name}`);
      handleCloseResetPasswordDialog();
    } catch (err: any) {
      console.error('Reset password error:', err.response?.data);
      snackbar.error(err.response?.data?.message || err.response?.data?.error || 'Failed to reset password');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          {isAdmin
            ? `Admin Dashboard - ${managementView === 'members' ? 'Member Management' : managementView === 'staff' ? 'Staff Management' : 'Non-Member Management'}`
            : `${managementView === 'members' ? 'Member' : managementView === 'staff' ? 'Staff' : 'Non-Member'} Directory`}
        </Typography>
        {!isAdmin && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Read-only directory — contact info for reaching members and staff.
          </Typography>
        )}
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {managementView === 'members'
            ? 'Manage all club members and their accounts'
            : managementView === 'staff'
            ? 'Manage staff, admins, and superusers'
            : 'Manage non-members, guest passes, and conversion prospects'}
        </Typography>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
          mb: 3
        }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder={managementView === 'members' ? 'Search members...' : managementView === 'staff' ? 'Search staff...' : 'Search non-members...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: '300px', md: '400px' },
                minWidth: '200px'
              }}
              size="small"
            />
            <FormControl sx={{ minWidth: { xs: '100%', sm: 180 } }} size="small">
              <InputLabel id="management-view-label">View</InputLabel>
              <Select
                labelId="management-view-label"
                id="management-view"
                value={managementView}
                label="View"
                onChange={(e) => setManagementView(e.target.value)}
              >
                <MenuItem value="members">Member Management</MenuItem>
                <MenuItem value="staff">Staff Management</MenuItem>
                <MenuItem value="non-members">Non-Member Management</MenuItem>
              </Select>
            </FormControl>
            {user?.role === 'superuser' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showDeactivated}
                    onChange={(e) => setShowDeactivated(e.target.checked)}
                    color="primary"
                  />
                }
                label="Show Deactivated"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              />
            )}
          </Box>
          {isAdmin && (managementView === 'staff' ? (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => {
                setNewMember({ ...initialMemberState, role: 'STAFF', member_type: 'PRIMARY' });
                setOpenAddDialog(true);
              }}
              sx={{ whiteSpace: 'nowrap', width: { xs: '100%', md: 'auto' } }}
            >
              Add Staff
            </Button>
          ) : (
            // One "Add" flow for members/non-members: the household form (handles a
            // single person OR a whole family). Adding someone to an EXISTING member
            // is the per-row "Add family member" action instead.
            <Button
              variant="contained"
              color={managementView === 'non-members' ? 'warning' : 'primary'}
              startIcon={<PersonAddIcon />}
              onClick={() => setOpenHouseholdDialog(true)}
              sx={{ whiteSpace: 'nowrap', width: { xs: '100%', md: 'auto' } }}
            >
              {managementView === 'members' ? 'Add Member' : 'Add Non-Member'}
            </Button>
          ))}
        </Box>

        {/* Summary line */}
        {!loading && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {managementView === 'members'
              ? `${householdCount.toLocaleString()} ${householdCount === 1 ? 'household' : 'households'} · ${peopleCount.toLocaleString()} ${peopleCount === 1 ? 'person' : 'people'}`
              : managementView === 'staff'
              ? `${displayList.length.toLocaleString()} staff`
              : `${displayList.length.toLocaleString()} non-members`}
          </Typography>
        )}

        {/* Mobile Card View */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Loading...</Typography>
            </Box>
          ) : displayList.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {managementView === 'members' ? 'No members found' : managementView === 'staff' ? 'No staff found' : 'No non-members found'}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {displayList.map((member: any) => {
                const household = managementView === 'members' ? householdMembersOf(member.id) : [];
                const peopleInHousehold =
                  managementView === 'non-members'
                    ? 1 + (member.family_count || 0)
                    : 1 + household.length;
                return (
                <Paper key={member.id} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, wordBreak: 'break-word' }}>
                          {member.first_name || member.firstName} {member.last_name || member.lastName}
                        </Typography>
                        {managementView !== 'staff' && (
                          <Chip
                            size="small"
                            label={`${peopleInHousehold} ${peopleInHousehold === 1 ? 'person' : 'people'}`}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                        {member.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {managementView === 'staff' ? (member.employee_number || 'N/A') : (member.member_number || 'N/A')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                      {managementView === 'members' ? (
                        <Chip
                          label={member.member_type ? member.member_type.charAt(0) + member.member_type.slice(1).toLowerCase() : 'N/A'}
                          color={member.member_type === 'PRIMARY' ? 'primary' : member.member_type === 'SECONDARY' ? 'info' : 'default'}
                          size="small"
                        />
                      ) : managementView === 'non-members' ? (
                        <>
                          <Chip label="NM Primary" color="warning" size="small" />
                          {member.membership_status === 'NON_MEMBER_BLOCKED' && (
                            <Chip label="Blocked" color="error" size="small" />
                          )}
                        </>
                      ) : (
                        <Chip
                          label={member.role.toUpperCase()}
                          color={getRoleColor(member.role)}
                          size="small"
                        />
                      )}
                    </Box>
                  </Box>

                  {managementView === 'staff' && member.departments && member.departments.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {member.departments.map((dept: string) => (
                        <Chip
                          key={dept}
                          label={dept.split('_').map((word: string) =>
                            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                          ).join(' ')}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Button
                      size="small"
                      startIcon={isRowExpanded(member.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      onClick={() => toggleRowExpansion(member.id)}
                    >
                      Details
                    </Button>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {renderMemberActions(member, 'small')}
                    </Box>
                  </Box>

                  <Collapse in={isRowExpanded(member.id)} timeout="auto" unmountOnExit>
                    {managementView === 'members' ? (
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                          Household
                        </Typography>
                        <Stack divider={<Divider flexItem />}>
                          {[member, ...household].map((m: any, idx: number) =>
                            renderRosterRow(m, { showActions: idx !== 0, size: 'small' })
                          )}
                        </Stack>
                      </Box>
                    ) : managementView === 'non-members' ? (
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                          Household
                        </Typography>
                        {renderNonMemberRoster(member, 'small')}
                      </Box>
                    ) : (
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                          Additional Details
                        </Typography>
                        <Stack spacing={1}>
                          {member.mobile_phone && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Mobile Phone</Typography>
                              <Typography variant="body2">{member.mobile_phone}</Typography>
                            </Box>
                          )}
                          {member.date_of_birth && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Birthday</Typography>
                              <Typography variant="body2">
                                {(() => {
                                  const [year, month, day] = member.date_of_birth.split('-');
                                  return `${month}/${day}/${year}`;
                                })()}
                              </Typography>
                            </Box>
                          )}
                          {(member.hire_date || member.joined_date) && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {managementView === 'members' ? 'Member Since' : 'Hire Date'}
                              </Typography>
                              <Typography variant="body2">
                                {(() => {
                                  const dateValue = member.hire_date || member.joined_date;
                                  const [year, month, day] = dateValue.split('-');
                                  return `${month}/${day}/${year}`;
                                })()}
                              </Typography>
                            </Box>
                          )}
                          {(member.address_line1 || member.city) && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Address</Typography>
                              <Typography variant="body2">
                                {`${member.address_line1 || ''}${member.address_line2 ? ', ' + member.address_line2 : ''}${member.city ? ', ' + member.city : ''}${member.state ? ', ' + member.state : ''} ${member.postal_code || ''}`.trim() || 'N/A'}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    )}
                  </Collapse>
                </Paper>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* Desktop Table View */}
        <TableContainer sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{managementView === 'members' ? 'Member #' : managementView === 'staff' ? 'Employee #' : 'Non-Member #'}</TableCell>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>{managementView === 'staff' ? 'Email' : 'Family'}</TableCell>
                <TableCell>{managementView === 'members' ? 'Member Type' : managementView === 'staff' ? 'Role' : 'Type'}</TableCell>
                {managementView === 'staff' && <TableCell>Department</TableCell>}
                {managementView === 'non-members' && <TableCell>Guest Visits</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={managementView === 'staff' ? 8 : 7} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : displayList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={managementView === 'staff' ? 8 : 7} align="center">
                    {managementView === 'members' ? 'No members found' : managementView === 'staff' ? 'No staff found' : 'No non-members found'}
                  </TableCell>
                </TableRow>
              ) : (
                displayList.map((member: any) => {
                  const household = managementView === 'members' ? householdMembersOf(member.id) : [];
                  const peopleInHousehold =
                    managementView === 'non-members'
                      ? 1 + (member.family_count || 0)
                      : 1 + household.length;
                  return (
                  <React.Fragment key={member.id}>
                    <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(member.id)}
                        >
                          {isRowExpanded(member.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                          {managementView === 'staff' ? (member.employee_number || 'N/A') : (member.member_number || 'N/A')}
                        </Typography>
                      </TableCell>
                      <TableCell>{member.first_name || member.firstName}</TableCell>
                      <TableCell>{member.last_name || member.lastName}</TableCell>
                      <TableCell>
                        {managementView === 'staff' ? (
                          member.email
                        ) : (
                          <Chip
                            size="small"
                            label={`${peopleInHousehold} ${peopleInHousehold === 1 ? 'person' : 'people'}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {managementView === 'members' ? (
                          <Chip
                            label={member.member_type ? member.member_type.charAt(0) + member.member_type.slice(1).toLowerCase() : 'N/A'}
                            color={member.member_type === 'PRIMARY' ? 'primary' : member.member_type === 'SECONDARY' ? 'info' : 'default'}
                            size="small"
                          />
                        ) : managementView === 'non-members' ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip label="NM Primary" color="warning" size="small" />
                            {member.membership_status === 'NON_MEMBER_BLOCKED' && (
                              <Chip label="Blocked" color="error" size="small" />
                            )}
                          </Box>
                        ) : (
                          <Chip
                            label={member.role.toUpperCase()}
                            color={getRoleColor(member.role)}
                            size="small"
                          />
                        )}
                      </TableCell>
                      {managementView === 'staff' && (
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {member.departments && member.departments.length > 0 ? (
                              member.departments.map((dept: string) => (
                                <Chip
                                  key={dept}
                                  label={dept.split('_').map((word: string) =>
                                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                                  ).join(' ')}
                                  size="small"
                                  variant="outlined"
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">N/A</Typography>
                            )}
                          </Box>
                        </TableCell>
                      )}
                      {managementView === 'non-members' && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isAdmin && (
                              <IconButton
                                size="small"
                                onClick={() => handleAdjustGuestVisits(member, -1)}
                                disabled={!member.guest_visits_this_year || member.guest_visits_this_year <= 0}
                                title="Remove 1 guest visit"
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            )}
                            <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center' }}>
                              {member.guest_visits_this_year || 0} / {member.guest_annual_limit || 6}
                            </Typography>
                            {isAdmin && (
                              <IconButton
                                size="small"
                                onClick={() => handleAdjustGuestVisits(member, 1)}
                                title="Add 1 guest visit"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        {renderMemberActions(member)}
                      </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                      <Collapse in={isRowExpanded(member.id)} timeout="auto" unmountOnExit>
                        {managementView === 'members' ? (
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Household
                            </Typography>
                            <Stack divider={<Divider flexItem />}>
                              {[member, ...household].map((m: any, idx: number) =>
                                renderRosterRow(m, { showActions: idx !== 0, size: 'medium' })
                              )}
                            </Stack>
                          </Box>
                        ) : managementView === 'non-members' ? (
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Household
                            </Typography>
                            {renderNonMemberRoster(member, 'medium')}
                          </Box>
                        ) : (
                        <>
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="h6" gutterBottom component="div">
                            Additional Details
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Mobile Phone
                              </Typography>
                              <Typography variant="body1">
                                {member.mobile_phone || 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Home Phone
                              </Typography>
                              <Typography variant="body1">
                                {member.home_phone || 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Work Phone
                              </Typography>
                              <Typography variant="body1">
                                {member.work_phone || 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Status
                              </Typography>
                              <Box>
                                {member.is_locked || member.isLocked ? (
                                  <Chip label="LOCKED" color="error" size="small" />
                                ) : (
                                  <Chip label="Active" color="success" size="small" />
                                )}
                              </Box>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Birthday
                              </Typography>
                              <Typography variant="body1">
                                {member.date_of_birth
                                  ? (() => {
                                      const [year, month, day] = member.date_of_birth.split('-');
                                      return `${month}/${day}/${year}`;
                                    })()
                                  : 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {managementView === 'members' ? 'Member Since' : 'Hire Date'}
                              </Typography>
                              <Typography variant="body1">
                                {(member.hire_date || member.joined_date)
                                  ? (() => {
                                      const dateValue = member.hire_date || member.joined_date;
                                      const [year, month, day] = dateValue.split('-');
                                      return `${month}/${day}/${year}`;
                                    })()
                                  : 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Primary Email
                              </Typography>
                              <Typography variant="body1">
                                {member.email || 'N/A'}
                              </Typography>
                            </Box>
                            <Box sx={{ gridColumn: '1 / -1' }}>
                              <Typography variant="body2" color="text.secondary">
                                Home Address
                              </Typography>
                              <Typography variant="body1">
                                {member.address_line1 || member.address_line2 || member.city || member.state || member.postal_code
                                  ? `${member.address_line1 || ''}${member.address_line2 ? ', ' + member.address_line2 : ''}${member.city ? ', ' + member.city : ''}${member.state ? ', ' + member.state : ''} ${member.postal_code || ''}`.trim()
                                  : 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Emergency Contact
                              </Typography>
                              <Typography variant="body1">
                                {member.emergency_contact_name || 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Emergency Phone
                              </Typography>
                              <Typography variant="body1">
                                {member.emergency_contact_phone || 'N/A'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Emergency Relationship
                              </Typography>
                              <Typography variant="body1">
                                {member.emergency_contact_relationship || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        </>
                        )}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <HouseholdDialog
        open={openHouseholdDialog}
        type={managementView === 'non-members' ? 'non-member' : 'member'}
        onClose={() => setOpenHouseholdDialog(false)}
        onCreated={fetchMembers}
      />

      <AddFamilyMemberDialog
        open={familyDialog.open}
        primaryId={familyDialog.primaryId}
        primaryName={familyDialog.primaryName}
        onClose={() => setFamilyDialog({ open: false, primaryId: '', primaryName: '' })}
        onAdded={fetchMembers}
      />

      <NonMemberFamilyDialog
        open={nonMemberFamilyDialog.open}
        nonMemberId={nonMemberFamilyDialog.nonMemberId}
        nonMemberName={nonMemberFamilyDialog.nonMemberName}
        onClose={() => setNonMemberFamilyDialog({ open: false, nonMemberId: '', nonMemberName: '' })}
        onAdded={() => {
          // Drop the cached roster for this non-member so the next expand
          // re-fetches and shows the new family member, then refresh counts.
          const targetId = nonMemberFamilyDialog.nonMemberId;
          setNonMemberFamily((prev) => {
            const next = { ...prev };
            delete next[targetId];
            return next;
          });
          fetchMembers();
        }}
      />

      {/* Add Member Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{managementView === 'members' ? 'Add New Member' : managementView === 'staff' ? 'Add New Staff' : 'Add New Non-Member'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={newMember.first_name}
                onChange={(e) => setNewMember({ ...newMember, first_name: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label="Last Name"
                value={newMember.last_name}
                onChange={(e) => setNewMember({ ...newMember, last_name: e.target.value })}
                required
              />
            </Box>

            {/* Role - only show in Staff Management */}
            {managementView === 'staff' && (
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newMember.role}
                  label="Role"
                  onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                >
                  {(user?.role === 'admin' || user?.role === 'superuser') && <MenuItem value="STAFF">Staff</MenuItem>}
                  {(user?.role === 'admin' || user?.role === 'superuser') && <MenuItem value="ADMIN">Admin</MenuItem>}
                  {user?.role === 'superuser' && <MenuItem value="SUPERUSER">Superuser</MenuItem>}
                </Select>
              </FormControl>
            )}

            {/* Departments - only show in Staff Management */}
            {managementView === 'staff' && (
              <FormControl fullWidth>
                <InputLabel>Departments</InputLabel>
                <Select
                  multiple
                  value={newMember.departments}
                  label="Departments"
                  onChange={(e) => setNewMember({ ...newMember, departments: e.target.value as string[] })}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                      },
                    },
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip
                          key={value}
                          label={value.split('_').map((word: string) =>
                            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                          ).join(' ')}
                          size="small"
                          onDelete={(e) => {
                            e.stopPropagation();
                            setNewMember({
                              ...newMember,
                              departments: newMember.departments.filter((d: string) => d !== value)
                            });
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      ))}
                      {selected.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Select departments...
                        </Typography>
                      )}
                    </Box>
                  )}
                >
                  <MenuItem value="front_desk">Front Desk</MenuItem>
                  <MenuItem value="management">Management</MenuItem>
                  <MenuItem value="human_resources">Human Resources</MenuItem>
                  <MenuItem value="accounting">Accounting</MenuItem>
                  <MenuItem value="it">IT</MenuItem>
                  <MenuItem value="marketing">Marketing</MenuItem>
                  <MenuItem value="membership_services">Membership Services</MenuItem>
                  <MenuItem value="swim_program">Swim Program</MenuItem>
                  <MenuItem value="tennis_program">Tennis Program</MenuItem>
                  <MenuItem value="pickleball_program">Pickleball Program</MenuItem>
                  <MenuItem value="golf_operations">Golf Operations</MenuItem>
                  <MenuItem value="gym">Gym</MenuItem>
                  <MenuItem value="kids_club">Kids Club</MenuItem>
                  <MenuItem value="spa">Spa</MenuItem>
                  <MenuItem value="cafe">Cafe</MenuItem>
                  <MenuItem value="bar">Bar</MenuItem>
                  <MenuItem value="restaurant">Restaurant</MenuItem>
                  <MenuItem value="catering">Catering</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="housekeeping">Housekeeping</MenuItem>
                  <MenuItem value="grounds">Grounds</MenuItem>
                  <MenuItem value="security">Security</MenuItem>
                  <MenuItem value="events">Events</MenuItem>
                  <MenuItem value="activities">Activities</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Click departments to select, click X on chips to remove
                </Typography>
              </FormControl>
            )}

            {/* Member Type - for MEMBER role and non-members */}
            {(newMember.role === 'MEMBER' || managementView === 'non-members') && (
              <FormControl fullWidth>
                <InputLabel>Member Type</InputLabel>
                <Select
                  value={newMember.member_type}
                  label="Member Type"
                  onChange={(e) => {
                    const newType = e.target.value;
                    // Clear relationship if it becomes invalid for the new member type
                    const validDependentRelationships = ['CHILD', 'OTHER'];
                    const validSecondaryRelationships = ['SPOUSE', 'PARTNER', 'OTHER'];

                    let shouldClearRelationship = false;
                    if (newType === 'DEPENDENT') {
                      shouldClearRelationship = !validDependentRelationships.includes(newMember.relationship_to_primary);
                    } else if (newType === 'SECONDARY') {
                      shouldClearRelationship = !validSecondaryRelationships.includes(newMember.relationship_to_primary);
                    }

                    setNewMember({
                      ...newMember,
                      member_type: newType,
                      // Reset dependent fields when switching to PRIMARY
                      primary_member_id: newType === 'PRIMARY' ? '' : newMember.primary_member_id,
                      relationship_to_primary: newType === 'PRIMARY' ? '' : (shouldClearRelationship ? '' : newMember.relationship_to_primary),
                    });
                  }}
                >
                  <MenuItem value="PRIMARY">Primary - Account Holder</MenuItem>
                  <MenuItem value="SECONDARY">Secondary - Spouse/Partner</MenuItem>
                  <MenuItem value="DEPENDENT">Dependent - Child/Other</MenuItem>
                </Select>
              </FormControl>
            )}
            {/* Primary Member/Non-Member Selection - for SECONDARY/DEPENDENT */}
            {(newMember.role === 'MEMBER' || managementView === 'non-members') && newMember.member_type !== 'PRIMARY' && (
              <FormControl fullWidth>
                <InputLabel>Primary Member</InputLabel>
                <Select
                  value={newMember.primary_member_id}
                  label="Primary Member"
                  onChange={(e) => {
                    const selectedPrimary = primaryMembers.find((p: any) => p.id === e.target.value);
                    setNewMember({
                      ...newMember,
                      primary_member_id: e.target.value,
                      membership_tier_id: selectedPrimary?.membership_tier_id || newMember.membership_tier_id,
                      // Auto-populate address fields from primary member
                      address_line1: selectedPrimary?.address_line1 || newMember.address_line1,
                      address_line2: selectedPrimary?.address_line2 || newMember.address_line2,
                      city: selectedPrimary?.city || newMember.city,
                      state: selectedPrimary?.state || newMember.state,
                      postal_code: selectedPrimary?.postal_code || newMember.postal_code,
                    });
                  }}
                  required
                >
                  <MenuItem value="">
                    <em>Select Primary Member</em>
                  </MenuItem>
                  {primaryMembers.map((primary: any) => (
                    <MenuItem key={primary.id} value={primary.id}>
                      {primary.first_name} {primary.last_name} ({primary.member_number || primary.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {/* Relationship - for SECONDARY/DEPENDENT */}
            {(newMember.role === 'MEMBER' || managementView === 'non-members') && newMember.member_type !== 'PRIMARY' && (
              <FormControl fullWidth>
                <InputLabel>Relationship to Primary</InputLabel>
                <Select
                  value={newMember.relationship_to_primary}
                  label="Relationship to Primary"
                  onChange={(e) => {
                    console.log('Selected relationship:', e.target.value);
                    setNewMember({ ...newMember, relationship_to_primary: e.target.value });
                  }}
                  required
                >
                  <MenuItem value="">
                    <em>Select Relationship</em>
                  </MenuItem>
                  {relationshipOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {/* Membership tier — only when tiers exist (flat-rate club usually has none) */}
            {newMember.role === 'MEMBER' && managementView !== 'non-members' && tiers.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Membership Tier</InputLabel>
                <Select
                  value={newMember.membership_tier_id}
                  label="Membership Tier"
                  onChange={(e) => setNewMember({ ...newMember, membership_tier_id: e.target.value })}
                  disabled={newMember.member_type !== 'PRIMARY'}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {tiers.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>{t.tier_name}</MenuItem>
                  ))}
                </Select>
                {newMember.member_type !== 'PRIMARY' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Inherited from primary member
                  </Typography>
                )}
              </FormControl>
            )}

            {/* Member/Employee Number field - not shown for non-members */}
            {managementView !== 'non-members' && (
              <TextField
                fullWidth
                label={managementView === 'members' ? 'Member Number' : 'Employee Number'}
                value={newMember.member_number || ''}
                onChange={(e) => setNewMember({ ...newMember, member_number: e.target.value })}
                helperText={managementView === 'members'
                  ? 'Leave blank to auto-generate (e.g., M000123)'
                  : 'Leave blank to auto-generate (e.g., E000123)'}
                placeholder={managementView === 'members' ? 'M000123' : 'E000123'}
              />
            )}

            {/* Member Since / Hired Date - not shown for non-members */}
            {managementView !== 'non-members' && (
              <TextField
                fullWidth
                label={newMember.role === 'STAFF' || newMember.role === 'ADMIN' || newMember.role === 'SUPERUSER'
                  ? 'Hired Date'
                  : 'Member Since'}
                type="date"
                value={newMember.joined_date}
                onChange={(e) => setNewMember({ ...newMember, joined_date: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={newMember.role === 'STAFF' || newMember.role === 'ADMIN' || newMember.role === 'SUPERUSER'
                  ? 'Date the employee was hired'
                  : 'Date the member joined the club'}
              />
            )}

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newMember.email}
              onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
              required={newMember.member_type !== 'DEPENDENT'}
              helperText={newMember.member_type === 'DEPENDENT' ? 'Optional for dependents (e.g., babies/young children)' : ''}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={newMember.password}
              onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
              required={newMember.member_type !== 'DEPENDENT'}
              helperText={newMember.member_type === 'DEPENDENT'
                ? 'Optional for dependents - auto-generated if left blank'
                : 'Must be at least 8 characters with uppercase, lowercase, and number'}
            />
            <TextField
              fullWidth
              label="Mobile Phone"
              value={newMember.mobile_phone}
              onChange={(e) => setNewMember({ ...newMember, mobile_phone: formatPhoneNumber(e.target.value) })}
            />
            {/* Hide Home Phone and Work Phone for dependents */}
            {newMember.member_type !== 'DEPENDENT' && (
              <>
                <TextField
                  fullWidth
                  label="Home Phone"
                  value={newMember.home_phone}
                  onChange={(e) => setNewMember({ ...newMember, home_phone: formatPhoneNumber(e.target.value) })}
                />
                <TextField
                  fullWidth
                  label="Work Phone"
                  value={newMember.work_phone}
                  onChange={(e) => setNewMember({ ...newMember, work_phone: formatPhoneNumber(e.target.value) })}
                />
              </>
            )}
            <TextField
              fullWidth
              label="Address Line 1"
              value={newMember.address_line1}
              onChange={(e) => setNewMember({ ...newMember, address_line1: e.target.value })}
              placeholder="123 Main St"
            />
            <TextField
              fullWidth
              label="Address Line 2"
              value={newMember.address_line2}
              onChange={(e) => setNewMember({ ...newMember, address_line2: e.target.value })}
              placeholder="Apt 4"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="City"
                value={newMember.city}
                onChange={(e) => setNewMember({ ...newMember, city: e.target.value })}
              />
              <TextField
                fullWidth
                label="State"
                value={newMember.state}
                onChange={(e) => setNewMember({ ...newMember, state: e.target.value })}
                placeholder="CA"
              />
              <TextField
                fullWidth
                label="Postal Code"
                value={newMember.postal_code}
                onChange={(e) => setNewMember({ ...newMember, postal_code: e.target.value })}
                placeholder="12345"
              />
            </Box>
            <TextField
              fullWidth
              label="Birthday"
              type="date"
              value={newMember.date_of_birth}
              onChange={(e) => setNewMember({ ...newMember, date_of_birth: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleAddMember} variant="contained" color="primary">
            {newMember.role === 'MEMBER' ? 'Add Member' : 'Add Staff'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMember?.role === 'MEMBER' || editMember?.role === 'member'
            ? 'Edit Member'
            : 'Edit Staff'}
        </DialogTitle>
        <DialogContent>
          {editMember && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {/* First/Last Name */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={editMember.first_name}
                  onChange={(e) => setEditMember({ ...editMember, first_name: e.target.value })}
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={editMember.last_name}
                  onChange={(e) => setEditMember({ ...editMember, last_name: e.target.value })}
                  required
                />
              </Box>

              {/* Role - only show in Staff Management */}
              {managementView === 'staff' && (
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={editMember.role}
                    label="Role"
                    onChange={(e) => setEditMember({ ...editMember, role: e.target.value })}
                  >
                    {(user?.role === 'admin' || user?.role === 'superuser') && <MenuItem value="STAFF">Staff</MenuItem>}
                    {(user?.role === 'admin' || user?.role === 'superuser') && <MenuItem value="ADMIN">Admin</MenuItem>}
                    {user?.role === 'superuser' && <MenuItem value="SUPERUSER">Superuser</MenuItem>}
                  </Select>
                </FormControl>
              )}

              {/* Departments - only show in Staff Management */}
              {managementView === 'staff' && (
                <FormControl fullWidth>
                  <InputLabel>Departments</InputLabel>
                  <Select
                    multiple
                    value={editMember.departments || []}
                    label="Departments"
                    onChange={(e) => setEditMember({ ...editMember, departments: e.target.value as string[] })}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                        },
                      },
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).length > 0 ? (
                          (selected as string[]).map((value) => (
                            <Chip
                              key={value}
                              label={value.split('_').map((word: string) =>
                                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                              ).join(' ')}
                              size="small"
                              onDelete={(e) => {
                                e.stopPropagation();
                                setEditMember({
                                  ...editMember,
                                  departments: editMember.departments.filter((d: string) => d !== value)
                                });
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Select departments...
                          </Typography>
                        )}
                      </Box>
                    )}
                  >
                    <MenuItem value="front_desk">Front Desk</MenuItem>
                    <MenuItem value="management">Management</MenuItem>
                    <MenuItem value="human_resources">Human Resources</MenuItem>
                    <MenuItem value="accounting">Accounting</MenuItem>
                    <MenuItem value="it">IT</MenuItem>
                    <MenuItem value="marketing">Marketing</MenuItem>
                    <MenuItem value="membership_services">Membership Services</MenuItem>
                    <MenuItem value="swim_program">Swim Program</MenuItem>
                    <MenuItem value="tennis_program">Tennis Program</MenuItem>
                    <MenuItem value="pickleball_program">Pickleball Program</MenuItem>
                    <MenuItem value="golf_operations">Golf Operations</MenuItem>
                    <MenuItem value="gym">Gym</MenuItem>
                    <MenuItem value="kids_club">Kids Club</MenuItem>
                    <MenuItem value="spa">Spa</MenuItem>
                    <MenuItem value="cafe">Cafe</MenuItem>
                    <MenuItem value="bar">Bar</MenuItem>
                    <MenuItem value="restaurant">Restaurant</MenuItem>
                    <MenuItem value="catering">Catering</MenuItem>
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="housekeeping">Housekeeping</MenuItem>
                    <MenuItem value="grounds">Grounds</MenuItem>
                    <MenuItem value="security">Security</MenuItem>
                    <MenuItem value="events">Events</MenuItem>
                    <MenuItem value="activities">Activities</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Click departments to select, click X on chips to remove
                  </Typography>
                </FormControl>
              )}

              {/* Member Type - only for MEMBER role */}
              {editMember.role === 'MEMBER' && (
                <FormControl fullWidth>
                  <InputLabel>Member Type</InputLabel>
                  <Select
                    value={editMember.member_type}
                    label="Member Type"
                    onChange={(e) => {
                      const newType = e.target.value;
                      // Clear relationship if it becomes invalid for the new member type
                      const validDependentRelationships = ['CHILD', 'OTHER'];
                      const validSecondaryRelationships = ['SPOUSE', 'PARTNER', 'OTHER'];

                      let shouldClearRelationship = false;
                      if (newType === 'DEPENDENT') {
                        shouldClearRelationship = !validDependentRelationships.includes(editMember.relationship_to_primary);
                      } else if (newType === 'SECONDARY') {
                        shouldClearRelationship = !validSecondaryRelationships.includes(editMember.relationship_to_primary);
                      }

                      setEditMember({
                        ...editMember,
                        member_type: newType,
                        // Reset dependent fields when switching to PRIMARY
                        primary_member_id: newType === 'PRIMARY' ? '' : editMember.primary_member_id,
                        relationship_to_primary: newType === 'PRIMARY' ? '' : (shouldClearRelationship ? '' : editMember.relationship_to_primary),
                      });
                    }}
                  >
                    <MenuItem value="PRIMARY">Primary - Account Holder</MenuItem>
                    <MenuItem value="SECONDARY">Secondary - Spouse/Partner</MenuItem>
                    <MenuItem value="DEPENDENT">Dependent - Child/Other</MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* Primary Member Selection - only for SECONDARY/DEPENDENT */}
              {editMember.role === 'MEMBER' && editMember.member_type !== 'PRIMARY' && (
                <FormControl fullWidth>
                  <InputLabel>Primary Member</InputLabel>
                  <Select
                    value={editMember.primary_member_id}
                    label="Primary Member"
                    onChange={(e) => {
                      const selectedPrimary = primaryMembers.find((p: any) => p.id === e.target.value);
                      setEditMember({
                        ...editMember,
                        primary_member_id: e.target.value,
                        membership_tier_id: selectedPrimary?.membership_tier_id || editMember.membership_tier_id,
                        // Auto-populate address fields from primary member
                        address_line1: selectedPrimary?.address_line1 || editMember.address_line1,
                        address_line2: selectedPrimary?.address_line2 || editMember.address_line2,
                        city: selectedPrimary?.city || editMember.city,
                        state: selectedPrimary?.state || editMember.state,
                        postal_code: selectedPrimary?.postal_code || editMember.postal_code,
                      });
                    }}
                    required
                  >
                    <MenuItem value="">
                      <em>Select Primary Member</em>
                    </MenuItem>
                    {primaryMembers.map((primary: any) => (
                      <MenuItem key={primary.id} value={primary.id}>
                        {primary.first_name} {primary.last_name} ({primary.member_number || primary.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Relationship - only for SECONDARY/DEPENDENT */}
              {editMember.role === 'MEMBER' && editMember.member_type !== 'PRIMARY' && (
                <FormControl fullWidth>
                  <InputLabel>Relationship to Primary</InputLabel>
                  <Select
                    value={editMember.relationship_to_primary}
                    label="Relationship to Primary"
                    onChange={(e) => {
                      console.log('Selected relationship:', e.target.value);
                      setEditMember({ ...editMember, relationship_to_primary: e.target.value });
                    }}
                    required
                  >
                    <MenuItem value="">
                      <em>Select Relationship</em>
                    </MenuItem>
                    {editRelationshipOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Membership tier — only when tiers exist (flat-rate club usually has none) */}
              {editMember.role === 'MEMBER' && tiers.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Membership Tier</InputLabel>
                  <Select
                    value={editMember.membership_tier_id}
                    label="Membership Tier"
                    onChange={(e) => setEditMember({ ...editMember, membership_tier_id: e.target.value })}
                    disabled={editMember.member_type !== 'PRIMARY'}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {tiers.map((t) => (
                      <MenuItem key={t.id} value={String(t.id)}>{t.tier_name}</MenuItem>
                    ))}
                  </Select>
                  {editMember.member_type !== 'PRIMARY' && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      Inherited from primary member
                    </Typography>
                  )}
                </FormControl>
              )}

              {/* Member/Employee Number */}
              <TextField
                fullWidth
                label={managementView === 'members' ? 'Member Number' : 'Employee Number'}
                value={editMember.member_number || ''}
                onChange={(e) => setEditMember({ ...editMember, member_number: e.target.value })}
                helperText={managementView === 'members'
                  ? 'Unique member identification number (e.g., M000123)'
                  : 'Unique employee identification number (e.g., E000123)'}
                placeholder={managementView === 'members' ? 'M000123' : 'E000123'}
              />

              {/* Member Since / Hired Date */}
              <TextField
                fullWidth
                label={editMember.role === 'STAFF' || editMember.role === 'ADMIN' || editMember.role === 'SUPERUSER'
                  ? 'Hired Date'
                  : 'Member Since'}
                type="date"
                value={editMember.joined_date}
                onChange={(e) => setEditMember({ ...editMember, joined_date: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={editMember.role === 'STAFF' || editMember.role === 'ADMIN' || editMember.role === 'SUPERUSER'
                  ? 'Date the employee was hired'
                  : 'Date the member joined the club'}
              />

              {/* Email */}
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editMember.email}
                onChange={(e) => setEditMember({ ...editMember, email: e.target.value })}
                required
              />

              {/* Phone Numbers */}
              <TextField
                fullWidth
                label="Mobile Phone"
                value={editMember.mobile_phone}
                onChange={(e) => setEditMember({ ...editMember, mobile_phone: formatPhoneNumber(e.target.value) })}
              />
              {/* Hide Home Phone and Work Phone for dependents */}
              {editMember.member_type !== 'DEPENDENT' && (
                <>
                  <TextField
                    fullWidth
                    label="Home Phone"
                    value={editMember.home_phone}
                    onChange={(e) => setEditMember({ ...editMember, home_phone: formatPhoneNumber(e.target.value) })}
                  />
                  <TextField
                    fullWidth
                    label="Work Phone"
                    value={editMember.work_phone}
                    onChange={(e) => setEditMember({ ...editMember, work_phone: formatPhoneNumber(e.target.value) })}
                  />
                </>
              )}

              {/* Address Fields */}
              <TextField
                fullWidth
                label="Address Line 1"
                value={editMember.address_line1}
                onChange={(e) => setEditMember({ ...editMember, address_line1: e.target.value })}
                placeholder="123 Main St"
              />
              <TextField
                fullWidth
                label="Address Line 2"
                value={editMember.address_line2}
                onChange={(e) => setEditMember({ ...editMember, address_line2: e.target.value })}
                placeholder="Apt 4"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="City"
                  value={editMember.city}
                  onChange={(e) => setEditMember({ ...editMember, city: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="State"
                  value={editMember.state}
                  onChange={(e) => setEditMember({ ...editMember, state: e.target.value })}
                  placeholder="CA"
                />
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={editMember.postal_code}
                  onChange={(e) => setEditMember({ ...editMember, postal_code: e.target.value })}
                  placeholder="12345"
                />
              </Box>

              {/* Birthday */}
              <TextField
                fullWidth
                label="Birthday"
                type="date"
                value={editMember.date_of_birth}
                onChange={(e) => setEditMember({ ...editMember, date_of_birth: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateMember} variant="contained" color="primary">
            {editMember?.role === 'MEMBER' || editMember?.role === 'member'
              ? 'Update Member'
              : 'Update Staff'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Non-Member Edit Dialog */}
      <Dialog
        open={openNonMemberEditDialog}
        onClose={() => {
          setOpenNonMemberEditDialog(false);
          setEditingNonMember(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Non-Member Settings</DialogTitle>
        <DialogContent>
          {editingNonMember && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {editingNonMember.member_number}
              </Typography>

              <TextField
                label="First Name"
                value={editingNonMember.first_name || ''}
                onChange={(e) => setEditingNonMember({ ...editingNonMember, first_name: e.target.value })}
                fullWidth
              />

              <TextField
                label="Last Name"
                value={editingNonMember.last_name || ''}
                onChange={(e) => setEditingNonMember({ ...editingNonMember, last_name: e.target.value })}
                fullWidth
              />

              <TextField
                label="Email"
                value={editingNonMember.email || ''}
                onChange={(e) => setEditingNonMember({ ...editingNonMember, email: e.target.value })}
                fullWidth
              />

              <TextField
                label="Phone"
                value={editingNonMember.phone || ''}
                onChange={(e) => setEditingNonMember({ ...editingNonMember, phone: e.target.value })}
                fullWidth
              />

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle1" fontWeight="bold">
                Guest Visit Settings
              </Typography>

              <TextField
                label="Annual Guest Visit Limit"
                type="number"
                value={editingNonMember.guest_annual_limit || 6}
                onChange={(e) => setEditingNonMember({ ...editingNonMember, guest_annual_limit: parseInt(e.target.value) || 0 })}
                fullWidth
                helperText={`Current visits: ${editingNonMember.guest_visits_this_year || 0}. Set the maximum number of guest visits allowed per year.`}
                inputProps={{ min: 0 }}
              />

              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Current Usage:</strong> {editingNonMember.guest_visits_this_year || 0} / {editingNonMember.guest_annual_limit || 6} visits used
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Remaining:</strong> {Math.max(0, (editingNonMember.guest_annual_limit || 6) - (editingNonMember.guest_visits_this_year || 0))} visits
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenNonMemberEditDialog(false);
              setEditingNonMember(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveNonMember}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={openResetPasswordDialog} onClose={handleCloseResetPasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {resetPasswordMember && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Typography variant="body1">
                Reset password for: <strong>{resetPasswordMember.name}</strong>
              </Typography>

              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                helperText="Must be at least 8 characters"
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                error={confirmPassword !== '' && newPassword !== confirmPassword}
                helperText={confirmPassword !== '' && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetPasswordDialog}>Cancel</Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="primary"
            disabled={!newPassword || newPassword !== confirmPassword}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboardPage;
